/**
 * Perimeter walls, and what a blast leaves of one (ROUND7 §3). The generator lays every wall run in pieces of
 * at most 1.5 m (`WALLS.pieceMax`; a compound's east and west faces are columns of 1.1 m pieces turned a
 * quarter), and each piece is a prop - and a mesh - of its own. So the model of ONE piece has to meet its
 * neighbours without a mark at the joint.
 *
 * The outline pass has no object ids: it draws a line where depth jumps or where the normal turns. Two
 * pieces that butt end to end have coplanar faces of one colour, so the joint itself draws nothing - as long
 * as nothing in the model belongs to the joint. Something did: every piece stood a pillar at its own west end
 * and stopped its arctic snow 15 cm short of both ends, so a row of short pieces had a post, or a notch, at
 * every joint - the 1.1 m column faces read as a picket fence, and 1.5 m runs would have done the same.
 * Now the pillars stand on a grid fixed to the WORLD, every `WALL_PILLAR_SPACING` along the run's own axis
 * (`wallAlong` says where a piece sits on it), so a row has them every four metres whatever its piece length,
 * and the coping and the snow run the full length. The body is sunk into the ground so a short piece seated
 * on a slope (at the height of its centre) does not show daylight under one end.
 *
 * Nothing is drawn past a piece's own ends, where the sim's collider stops: a pillar that straddles a joint
 * is drawn half by each piece - whole where both stand, cut flush where one is gone (a gate, a breach, the
 * end of the run) - and the coping stops at the end instead of overhanging it.
 */

import type { Pcg32 } from '../../sim/rng';
import type { Palette } from '../palette';
import { MeshBuilder, place } from './MeshBuilder';
import { capColor } from './snow';

/** Metres between pillars along a run, on a grid fixed to the world. */
export const WALL_PILLAR_SPACING = 4;
/** Height of the wall body; the coping sits on it. */
export const WALL_BODY_HEIGHT = 1.7;
/** How far the body and pillars go into the ground below the piece's seat. */
const FOOTING = 0.3;
/** Half a pillar's width. */
const PILLAR_HALF = 0.35;

/** Brick in temperate country, the biome's concrete elsewhere: the same as the ruins. */
export const masonry = (p: Palette): string => (p.biome === 'temperate' ? 'brick' : 'concrete');

/**
 * Where a run piece placed at (x, z) turned `yaw` sits along its own axis: the world coordinate of its
 * centre along its local +x, which `place` turns to (cos yaw, 0, -sin yaw).
 */
export function wallAlong(x: number, z: number, yaw: number): number {
  return x * Math.cos(yaw) - z * Math.sin(yaw);
}

/**
 * The part of every pillar that falls on a piece `length` long centred `along` metres along its axis, as
 * [from, to] in the piece's own x. The pillars stand on the world grid; each piece draws only its own share.
 */
export function pillarSpans(along: number, length: number, spacing = WALL_PILLAR_SPACING): Array<[number, number]> {
  const lo = along - length / 2;
  const hi = along + length / 2;
  const out: Array<[number, number]> = [];
  for (let k = Math.ceil((lo - PILLAR_HALF) / spacing); k * spacing - PILLAR_HALF < hi; k++) {
    const from = Math.max(k * spacing - PILLAR_HALF, lo);
    const to = Math.min(k * spacing + PILLAR_HALF, hi);
    if (to - from > 1e-3) out.push([from - along, to - along]);
  }
  return out;
}

/** One piece of perimeter wall along x, `along` metres along its run (see `wallAlong`; 0 for a lone piece). */
export function wallRun(p: Palette, _rng: Pcg32, length = 8, along = 0): MeshBuilder {
  const mb = new MeshBuilder();
  const body = masonry(p);
  mb.box(length, WALL_BODY_HEIGHT + FOOTING, 0.5, p.color(`${body}.mid`), place(0, -FOOTING, 0));
  mb.box(length, 0.22, 0.7, p.color(`${body}.hi`), place(0, WALL_BODY_HEIGHT, 0));
  if (p.biome === 'arctic') mb.box(length, 0.1, 0.4, p.color('ground.3'), place(0, WALL_BODY_HEIGHT + 0.22, 0));
  // Pillars stand proud of the coping - and of its snow in the arctic, with their own dusting on top.
  const pillar = { all: p.color(`${body}.lo`), top: capColor(p, `${body}.mid`) };
  const top = p.biome === 'arctic' ? 2.1 : 2.0;
  for (const [from, to] of pillarSpans(along, length)) {
    mb.box(to - from, top + FOOTING, 0.7, pillar, place((from + to) / 2, -FOOTING, 0));
  }
  return mb;
}

/**
 * What a LAW or a bomb leaves of one piece: the wall knocked down to a ragged line of broken courses well
 * under knee height, its rubble spilled out to both sides, and a broken stub standing at each end - so the
 * wall either side of a breach ends in a broken edge, not a clean cut, and the gap reads as a way through.
 * Everything is in the piece's own frame (along x), so the world turns it with the piece.
 */
export function wallBreach(p: Palette, rng: Pcg32, length: number): MeshBuilder {
  const mb = new MeshBuilder();
  const m = masonry(p);
  // Freshly broken tops take the light step (dusted white in the arctic), broken ends the dark one.
  const end = p.color(`${m}.lo`);
  const broken = { all: p.color(`${m}.mid`), top: capColor(p, `${m}.hi`), left: end, right: end };
  // The spill: a low spread of dust and fragments, wider than the wall was thick.
  mb.box(length, 0.1, 1.5, { all: p.color(`${m}.lo`), top: p.color(`${m}.lo`) });
  // The footing courses, broken off at two heights.
  const split = rng.range(-0.2, 0.2) * length;
  mb.box(length / 2 + split, rng.range(0.4, 0.6), 0.5, broken, place(-length / 4 + split / 2, -FOOTING, 0));
  mb.box(length / 2 - split, rng.range(0.35, 0.55), 0.5, broken, place(length / 4 + split / 2, -FOOTING, 0));
  // A stub at each end: a lower block and a narrower one on top of it, leaning, so the break is ragged.
  for (const side of [-1, 1]) {
    const h = rng.range(0.45, 0.8);
    const x = side * (length / 2 - 0.14);
    mb.box(0.28, h + FOOTING, 0.5, broken, place(x, -FOOTING, 0));
    const lean = side * rng.range(0.12, 0.3); // toward the gap
    mb.box(0.18, rng.range(0.15, 0.3), 0.42, broken, place(x + side * 0.04, h - 0.04, 0, 0, 1, 0, lean));
  }
  // Fragments of body and coping thrown out either side, one scorched - kept inside the gap, clear of the
  // wall standing either side of it.
  const count = Math.min(30, Math.max(4, Math.round(length * 3)));
  const spread = Math.max(0, length / 2 - 0.3);
  for (let i = 0; i < count; i++) {
    const tone = i === 0 ? 'ink.1' : rng.chance(0.3) ? `${m}.hi` : rng.chance(0.5) ? `${m}.mid` : `${m}.lo`;
    const side = rng.chance(0.5) ? 1 : -1;
    const at = place(
      rng.range(-spread, spread), 0.05, side * rng.range(0.15, 0.75),
      rng.range(0, Math.PI * 2), 1, rng.range(-0.3, 0.3), rng.range(-0.3, 0.3),
    );
    mb.box(rng.range(0.22, 0.45), rng.range(0.12, 0.26), rng.range(0.18, 0.36), p.color(tone), at);
  }
  return mb;
}

/**
 * A row laid the way the generator lays one - `count` pieces `piece` long end to end along x, centred on the
 * origin - with the pieces at `breached` already blown: for the turntable, where the joints and a breach can
 * be looked at together. Merged into one mesh, which the outline pass cannot tell from separate ones.
 */
export function wallRowSample(
  p: Palette, rng: Pcg32, count: number, piece: number, breached: readonly number[],
): MeshBuilder {
  const mb = new MeshBuilder();
  for (let i = 0; i < count; i++) {
    const x = (i - (count - 1) / 2) * piece;
    const model = breached.includes(i) ? wallBreach(p, rng.fork(`breach${i}`), piece) : wallRun(p, rng, piece, x);
    mb.merge(model, place(x, 0, 0));
  }
  return mb;
}
